import * as React from 'react';
import { PageStrip } from './PageStrip';
import { st, classes } from './CorePagination.st.css';
import { measureAndSetRootMinWidth } from './RootMinWidth';
import { PaginationDataHooks } from './DataHooks';
import { filterDataProps } from '../../utils/FilterDataProps';
const upperCaseFirst = (str) => str[0].toUpperCase() + str.slice(1);
export const getId = (idPrefix = '', name = '') => idPrefix ? idPrefix + name : null;
export const calculateWidth = (totalPages) => `${totalPages.toString().length}em`;
export class CorePagination extends React.Component {
    constructor() {
        super(...arguments);
        this.getMaxPagesToShow = () => {
            if (this.props.maxPagesToShow) {
                return this.props.maxPagesToShow;
            }
            if (this.props.responsive) {
                return 20;
            }
            return 7;
        };
        this.state = {
            pageInputValue: String(this.props.currentPage),
            pageInputHasError: false,
        };
        this.handlePageInputChange = (e) => {
            this.setState({
                pageInputValue: e.target.value,
                pageInputHasError: false,
            });
        };
        this.handlePageInputKeyDown = (event) => {
            // Enter
            if (event.key === 'Enter') {
                const page = Number(this.state.pageInputValue);
                if (page !== this.props.currentPage) {
                    if (1 <= page && page <= this.props.totalPages) {
                        this.props.onChange({ event, page });
                    }
                    else {
                        this.setState({ pageInputHasError: true });
                    }
                }
            }
        };
        this.handlePageInputBlur = () => {
            this.setState({
                pageInputValue: String(this.props.currentPage),
                pageInputHasError: false,
            });
        };
        this.handlePageClick = (event, page) => {
            this.props.onChange({ event, page });
        };
        this.handlePageKeyDown = (event, page) => {
            // Enter or Space
            if (event.key === 'Enter' || event.key === 'Space') {
                this.props.onChange({ event, page });
            }
        };
    }
    updateRootMinWidth() {
        measureAndSetRootMinWidth(this.rootNode, this.props.paginationMode, this.props.id);
    }
    componentDidMount() {
        this.props.updateResponsiveLayout && this.updateRootMinWidth();
    }
    componentDidUpdate() {
        this.props.updateResponsiveLayout && this.updateRootMinWidth();
    }
    getId(elementName = '') {
        return getId(this.props.id, elementName);
    }
    renderPageStrip() {
        return (React.createElement(PageStrip, { id: this.props.id, totalPages: this.props.totalPages, currentPage: this.props.currentPage, maxPagesToShow: this.getMaxPagesToShow(), showFirstPage: this.props.showFirstPage, showLastPage: this.props.showLastPage, responsive: this.props.responsive, pageUrl: this.props.pageUrl, gapLabel: this.props.gapLabel, onPageClick: this.handlePageClick, onPageKeyDown: this.handlePageKeyDown, updateResponsiveLayout: this.props.updateResponsiveLayout, disabled: this.props.disabled }));
    }
    renderPages() {
        switch (this.props.paginationMode) {
            case "input" /* PaginationMode.Input */:
                return this.renderPageForm();
            case "compact" /* PaginationMode.Compact */:
                return this.renderPageCompact();
            case "pages" /* PaginationMode.Pages */:
            default:
                return this.renderPageStrip();
        }
    }
    renderPageCompact() {
        return (React.createElement("div", { "data-hook": PaginationDataHooks.pageCompact, id: this.getId('pageCompact'), className: classes.compact },
            React.createElement("span", { id: this.getId('currentPage'), "data-hook": PaginationDataHooks.currentPage }, this.props.currentPage),
            React.createElement("span", { id: this.getId('slash'), className: classes.slash, "data-hook": PaginationDataHooks.slashLabel }, this.props.slashLabel),
            React.createElement("span", { id: this.getId('totalPages'), "data-hook": PaginationDataHooks.totalPages }, this.props.totalPages)));
    }
    renderPageForm() {
        return (React.createElement("div", { "data-hook": PaginationDataHooks.pageForm, id: this.getId('pageForm'), className: classes.pageForm, dir: "ltr" },
            React.createElement("input", { id: this.getId('pageInput'), "data-hook": PaginationDataHooks.pageInput, type: "number", className: classes.pageInput, min: 1, max: this.props.totalPages, value: this.state.pageInputValue, disabled: this.props.disabled, onChange: this.handlePageInputChange, onKeyDown: this.handlePageInputKeyDown, "aria-label": 'Page number, select a number between 1 and ' +
                    this.props.totalPages, onBlur: this.handlePageInputBlur, style: { width: calculateWidth(this.props.totalPages) } }),
            this.props.showInputModeTotalPages && [
                React.createElement("span", { key: "slash", id: this.getId('slash'), className: classes.slash }, this.props.slashLabel),
                React.createElement("span", { key: "total-pages", id: this.getId('totalPages'), "data-hook": PaginationDataHooks.totalPages, className: classes.totalPages }, this.props.totalPages),
            ]));
    }
    renderEmptyButton(type) {
        const btnClass = {
            ["previous" /* ButtonType.Prev */]: [classes.emptyButtonPrevious],
            ["next" /* ButtonType.Next */]: [classes.emptyButtonNext],
        }[type];
        return (React.createElement("div", { "data-hook": PaginationDataHooks[type], className: st(classes.emptyButton, ...btnClass) }));
    }
    renderNavButton(type) {
        const { currentPage, totalPages, pageUrl } = this.props;
        const disabled = this.props.disabled ||
            ((type === "first" /* ButtonType.First */ || type === "previous" /* ButtonType.Prev */) &&
                currentPage <= 1) ||
            ((type === "last" /* ButtonType.Last */ || type === "next" /* ButtonType.Next */) &&
                currentPage >= totalPages);
        const [btnClass, label, page] = {
            ["previous" /* ButtonType.Prev */]: [
                classes.navButtonPrevious,
                this.props.previousLabel,
                currentPage - 1,
            ],
            ["next" /* ButtonType.Next */]: [
                classes.navButtonNext,
                this.props.nextLabel,
                currentPage + 1,
            ],
            ["first" /* ButtonType.First */]: [classes.navButtonFirst, this.props.firstLabel, 1],
            ["last" /* ButtonType.Last */]: [
                classes.navButtonLast,
                this.props.lastLabel,
                totalPages,
            ],
        }[type];
        return (React.createElement("a", { "data-hook": PaginationDataHooks[type], id: this.getId('navButton' + upperCaseFirst(type)), className: st(classes.navButton, { disabled }, btnClass), role: "button", "aria-label": upperCaseFirst(type) + ' Page', "aria-disabled": disabled, tabIndex: disabled || pageUrl ? null : 0, onClick: disabled ? null : (event) => this.handlePageClick(event, page), onKeyDown: disabled ? null : (event) => this.handlePageKeyDown(event, page), href: !disabled && pageUrl ? pageUrl(page) : null }, label));
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            pageInputValue: String(nextProps.currentPage),
            pageInputHasError: false,
        });
    }
    render() {
        const { disabled, showFirstLastNavButtons, showNextLabel, showPreviousLabel, width, style: inlineStyle, } = this.props;
        const styleStates = {
            disabled,
            error: this.state.pageInputHasError,
        };
        return (React.createElement("nav", { ref: (el) => (this.rootNode = el), id: this.getId(''), "aria-label": "Pagination Navigation", "aria-disabled": disabled, dir: this.props.rtl ? 'rtl' : null, onClick: this.props.onClick, onDoubleClick: this.props.onDoubleClick, onMouseEnter: this.props.onMouseEnter, onMouseLeave: this.props.onMouseLeave, style: inlineStyle || { width }, className: st(classes.root, styleStates, this.props.className), ...filterDataProps(this.props) },
            showFirstLastNavButtons && this.renderNavButton("first" /* ButtonType.First */),
            showPreviousLabel
                ? this.renderNavButton("previous" /* ButtonType.Prev */)
                : this.renderEmptyButton("previous" /* ButtonType.Prev */),
            this.renderPages(),
            showNextLabel
                ? this.renderNavButton("next" /* ButtonType.Next */)
                : this.renderEmptyButton("next" /* ButtonType.Next */),
            showFirstLastNavButtons && this.renderNavButton("last" /* ButtonType.Last */)));
    }
}
CorePagination.displayName = 'CorePagination';
CorePagination.defaultProps = {
    currentPage: 1,
    showFirstLastNavButtons: false,
    showFirstPage: false,
    showLastPage: false,
    showNextLabel: true,
    showPreviousLabel: true,
    responsive: false,
    paginationMode: 'pages',
    showInputModeTotalPages: false,
    disabled: false,
    // dir="rtl" automatically flips the direction of less-than and more-than signs.
    // If we decide to use different labels we need to add conditional logic.
    firstLabel: '<<',
    lastLabel: '>>',
    previousLabel: '<',
    nextLabel: '>',
    gapLabel: '...',
    slashLabel: '\u00A0/\u00A0',
};
//# sourceMappingURL=CorePagination.js.map